import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getToken, clearSession } from './storage';
import { isUserOfflineMode, trackRequestStart, reportRequestOutcome, setReachabilityProbe } from './networkStatus';
import { telemetryHttp, telemetryObservedPublicIp } from './telemetry';
import type { DeliveryNote, OcrResult } from '../types';

const BASE_URL = __API_BASE_URL__;

// Símbolo para guardar el `done()` callback del trackRequestStart en el
// config del request, así el response interceptor puede llamarlo.
const TRACK_KEY = '__networkTrackDone__';
const START_KEY = '__networkTrackStart__';

function createClient(baseURL: string): AxiosInstance {
  // Timeout por defecto de las LECTURAS: 10s. Antes eran 15s, que en "lie-fi"
  // (red sin salida real) dejaban la petición colgada demasiado tiempo. Con 10s
  // el fallo se detecta antes y la pantalla cae a la caché rápido. Las
  // escrituras/subidas mantienen su override propio (createDetail 45s,
  // uploadImages 120s…) y el login lo sube a 15s (tolera cold start del back).
  const client = axios.create({ baseURL, timeout: 10000 });

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Si el usuario decidió trabajar offline, abortamos antes de salir.
    // El error resultante cae al catch existente de la pantalla → caché +
    // encolar. Sin tocar la lógica de las pantallas.
    if (isUserOfflineMode()) {
      const ac = new AbortController();
      ac.abort();
      config.signal = ac.signal;
      return config;
    }

    // Tracking de latencia + AbortController gestionado por networkStatus.
    // Si la petición ya tiene signal (caso raro), respetamos el suyo y solo
    // medimos tiempo.
    const tracker = trackRequestStart();
    if (!config.signal) {
      config.signal = tracker.controller.signal;
    }
    (config as unknown as Record<string, unknown>)[TRACK_KEY] = tracker.done;
    (config as unknown as Record<string, unknown>)[START_KEY] = Date.now();
    return config;
  });

  client.interceptors.response.use(
    (r) => {
      const cfg = r.config as unknown as Record<string, unknown>;
      const done = cfg[TRACK_KEY] as ((ms?: number) => void) | undefined;
      const startedAt = cfg[START_KEY] as number | undefined;
      if (done) done(startedAt ? Date.now() - startedAt : undefined);
      // Llegó respuesta del servidor → hay conexión real (aunque sea un error
      // HTTP): limpia el contador de "servidor inalcanzable".
      reportRequestOutcome(true);
      // Telemetría en vivo: método, ruta, código y duración. Jamás cuerpos,
      // tokens ni cabeceras.
      telemetryHttp(
        (r.config.method || 'GET').toUpperCase(),
        r.config.url || '',
        r.status,
        startedAt ? Date.now() - startedAt : 0
      );
      telemetryObservedPublicIp(r.headers['x-observed-client-ip']);
      return r;
    },
    async (error) => {
      const cfg = error.config as unknown as Record<string, unknown> | undefined;
      const done = cfg?.[TRACK_KEY] as ((ms?: number) => void) | undefined;
      // En error no aportamos latencia (no es representativa).
      if (done) done();
      const startedAt = cfg?.[START_KEY] as number | undefined;
      telemetryHttp(
        (error.config?.method || 'GET').toUpperCase(),
        error.config?.url || '',
        error.response?.status ?? -1,
        startedAt ? Date.now() - startedAt : 0,
        error.code || error.name
      );
      telemetryObservedPublicIp(error.response?.headers?.['x-observed-client-ip']);
      // Señal real de conexión: si hubo respuesta HTTP (4xx/5xx) el servidor
      // está accesible; si NO hubo respuesta (timeout / red caída / lie-fi) lo
      // marcamos como inalcanzable. Los aborts del modo offline manual no
      // cuentan (los excluimos), para no ensuciar el indicador.
      //
      // Se comprueba el código además del modo: al salir de offline manual, las
      // peticiones que setUserOfflineMode abortó llegan aquí con el modo YA
      // desactivado, y sin esto contarían como servidor inalcanzable — bastaban
      // 2 para pintar la cobertura como DEFICIENTE nada más recuperar la red.
      const cancelada = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
      if (!isUserOfflineMode() && !cancelada) {
        const noResponse = !!error?.isAxiosError && error.response == null;
        reportRequestOutcome(!noResponse, startedAt);
      }
      if (error.response?.status === 401) {
        // Token caducado / inválido. NO llamamos al endpoint Unsubscribe
        // porque también daría 401. Solo invalidamos la subscripción del
        // navegador: en cuanto el back intente el siguiente broadcast a
        // este dispositivo recibirá 410 Gone y el sender marcará
        // IsActive=false automáticamente.
        // Dynamic import para evitar el ciclo api ↔ pushNotifications.
        try {
          const mod = await import('./pushNotifications');
          await mod.unsubscribeLocalPush();
        } catch { /* best-effort */ }
        clearSession();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return client;
}

export const api = createClient(BASE_URL);

// Sonda de alcance para networkStatus: antes de pintar "Sin conexión" se
// comprueba con UNA petición ligera que el servidor de verdad no contesta.
// Va con fetch y no con axios para no esperar en la cola de peticiones de la
// pantalla ni contar en sus estadísticas, y el SW la deja pasar sin caché (ver
// sw.ts): una respuesta vieja de la caché no demuestra que haya red. Cualquier
// respuesta HTTP vale como "hay conexión"; solo un fallo de red o el plazo
// agotado cuentan como "no".
export const PROBE_PATH = 'api/Cargo/GetCargoByDatePing';
const PROBE_TIMEOUT_MS = 8000;
setReachabilityProbe(async () => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(`${BASE_URL}${PROBE_PATH}`, { cache: 'no-store', signal: controller.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
});

// Auth
// Timeout más holgado (15s) que el default de lecturas (10s): el back puede
// tener cold start y el login es la única puerta de entrada.
export const login = (body: object) =>
  api.post<string>('api/Auth', body, { timeout: 15000 });

// Positions
export const getPositionsByDate = (year: number, month: number, day: number) =>
  api.get(`api/Cargo/GetCargoByDateWithClaimsAsync/${year}/${month}/${day}`);

// Totales de contenedores por día de un mes (para la vista calendario): una
// sola petición agrupada por día en vez de una por día.
export interface CargoDayTotal { day: number; totalContainers: number; loads: number; loadedContainers?: number; }
export const getCargoMonthTotals = (year: number, month: number) =>
  api.get<CargoDayTotal[]>(`api/Cargo/GetCargoMonthTotalsWithClaimsAsync/${year}/${month}`);

// Todas las cargas del mes con detalle (buscador del calendario): 1 sola
// petición y el filtro por texto se hace en el cliente.
export const getCargoByMonth = (year: number, month: number) =>
  api.get(`api/Cargo/GetCargoByMonthWithClaimsAsync/${year}/${month}`);

// ── Creación self-managed ──────────────────────────────────────────────────
// Reference data para el formulario de creación de planning (productos/fábricas).
export interface ProductOption { idProduct: number; name: string | null; }
export interface FactoryOption { idFactory: number; name: string | null; }
export const getAllProducts = () =>
  api.get<ProductOption[]>('api/Product/GetAllProduct');

// ── Catálogo de productos self-managed ────────────────────────────────────
// Tabla propia (SelfManagedProduct), separada de la maestra clásica. El
// servidor filtra por los países DEL TOKEN y devuelve ya deduplicado: un
// producto presente en varios países aparece una sola vez.
export interface SelfManagedProductOption {
  idSelfManagedProduct: number;
  name: string;
  // Países (ISO-2) en los que el producto está dado de alta. Viajan al cliente
  // para poder filtrar al cambiar el desplegable de país sin repetir la
  // petición — y para no ofrecer nunca un producto que el servidor rechazaría.
  countryCodes: string[];
}
export const getMySelfManagedProducts = () =>
  api.get<SelfManagedProductOption[]>('api/SelfManagedProduct/GetMyProductsAsync');

// Alta de producto por el propio controlador, cuando el catálogo se queda
// corto. Si ya existe (mismo nombre normalizado) el servidor NO duplica:
// devuelve el existente tras asegurar su relación con el país.
export const createSelfManagedProduct = (name: string, countryCode: string) =>
  api.post<SelfManagedProductOption>('api/SelfManagedProduct/CreateAsync', { name, countryCode });

// ── Proveedores (texto libre que aprende) ─────────────────────────────────
// No hay endpoint de alta: el proveedor se registra como efecto de crear la
// planning. Aquí solo se consultan sugerencias de lo ya escrito antes.
export interface SupplierSuggestion {
  idSupplier: number;
  name: string;
  // Última dirección usada con ese proveedor en ese país. Permite proponerla
  // al elegirlo, sin volver a teclearla (se usa en la fase de ubicación).
  lastAddress?: string | null;
  lastLatitude?: number | null;
  lastLongitude?: number | null;
}
// countryCode acota las sugerencias al país de la planning que se está creando:
// al crear una de Pakistán no tiene sentido sugerir proveedores de Italia.
export const searchSuppliers = (q: string, countryCode?: string, take = 10) =>
  api.get<SupplierSuggestion[]>('api/Supplier/SearchAsync', { params: { q, countryCode, take } });

// ── Geocodificación (a través de NUESTRA API) ─────────────────────────────
// No se llama a Azure Maps desde el navegador: la clave se quedaría expuesta en
// el bundle y la cuenta exige lista blanca de orígenes CORS (cualquier dominio
// o puerto no listado devuelve 403). Con el servidor de por medio no hay origen
// que declarar y la clave no sale de ahí.
export interface ReverseGeocodeResult {
  address: string | null;
  municipality?: string | null;
  countryCode?: string | null;
  postalCode?: string | null;
}
export const reverseGeocodeApi = (lat: number, lng: number, language?: string) =>
  api.get<ReverseGeocodeResult>('api/Geocoding/ReverseAsync', {
    params: { lat, lng, language },
    timeout: 15000,
  });

export interface GeocodeSearchResult {
  latitude: number | null;
  longitude: number | null;
  country?: string | null;
  countryCode?: string | null;
}
export const geocodeSearchApi = (q: string, countryCode?: string, language?: string) =>
  api.get<GeocodeSearchResult>('api/Geocoding/SearchAsync', {
    params: { q, countryCode, language },
    timeout: 15000,
  });
export const getAllFactories = () =>
  api.get<FactoryOption[]>('api/Factory/GetAllFactory');

// Crea la planning mínima + la cargo del día en una sola llamada. El país se
// valida en el servidor (∈ self-managed del token). Online-only.
export interface CreateSelfManagedPlanningBody {
  year: number;
  month: number;
  day: number;
  idProduct: number;
  // Producto del catálogo self-managed. Cuando va informado, el servidor pone
  // un placeholder en la FK clásica (Cargo.IdProduct) y guarda este como el
  // producto real. Sin él, comportamiento anterior con idProduct.
  idSelfManagedProduct?: number;
  idFactory: number;
  // Proveedor en texto libre (self-managed). Se guarda en planning y cargo, y
  // alimenta la tabla de proveedores para autocompletar la próxima vez.
  supplierName?: string;
  // Ubicación de la carga (texto libre, puede venir del GPS). Se guarda en la
  // carga y se aprende asociada al proveedor + país.
  locationText?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  totalContainers: number;
  idBookingPortic: string;
  countryCode: string;
}
export interface CreateSelfManagedPlanningResult { idPlanning: number; idCargo: number; }
export const createSelfManagedPlanning = (body: CreateSelfManagedPlanningBody) =>
  api.post<CreateSelfManagedPlanningResult>('api/Planning/CreateSelfManagedPlanning', body, {
    timeout: 45000,
  });

export const editCargoComment = (idCargo: number, body: object) =>
  api.post(`api/Cargo/PostEditCargoAsync/${idCargo}`, body);

// Details
export const getDetailsByCargo = (idCargo: number, showLoaded = true) =>
  api.get(`api/CargoDetail/GetCargoDetailByIdCargoAppControllerAsync/${idCargo}`, {
    params: { showStatusCargada: showLoaded },
  });

// Timeouts más generosos en endpoints críticos para la cola offline. En
// redes 3G débiles, 15s no llegan para un POST con payload mediano + ACK
// del servidor lento — y un timeout aquí provoca bump de retries y, peor,
// la posibilidad de duplicado en NEW (el server procesó pero no leemos
// la respuesta). 45s da margen real sin bloquear demasiado la UI.
//
// Los endpoints de escritura aceptan un override de config (p. ej. timeout)
// para que el guardado en PRIMER PLANO pueda acotar su espera sin tocar los
// timeouts generosos que necesita la cola offline: allí nadie está mirando la
// pantalla y lo único que importa es que el envío llegue.
export const createDetail = (idCargo: number, body: object, config?: AxiosRequestConfig) =>
  api.post(`api/CargoDetail/CreateCargoDetailAppController/${idCargo}`, body, {
    timeout: 45000,
    ...config,
  });

export const editDetail = (idCargoDetail: number, body: object, config?: AxiosRequestConfig) =>
  api.post(`api/CargoDetail/EditCargoDetailAppController/${idCargoDetail}`, body, {
    timeout: 45000,
    ...config,
  });

export const deleteDetail = (idCargoDetail: number) =>
  api.delete(`api/CargoDetail/deleteCargoDetail/${idCargoDetail}`, {
    timeout: 30000,
  });

export const validateContainer = (containerNumber: string, config?: AxiosRequestConfig) =>
  api.get(`api/CargoDetail/validate/container/${containerNumber}`, config);

// Informe de la carga por correo (.docx con ficha y fotos), al correo del
// propio usuario. El servidor contesta 202 al instante y lo genera después;
// 429 si esa misma lista ya se pidió el tope de veces hoy. Solo se manda la
// lista de contenedores que hay en pantalla: el servidor decide el resto.
// El informe y el correo van siempre en inglés, sea cual sea el idioma de la
// app (es el idioma común de la empresa): por eso no se manda el idioma.
export interface CargoReportRequestResponse {
  requestId?: string;
  email?: string;
  detailCount: number;
  sentToday: number;
  dailyLimit: number;
  nextAllowedAtUtc?: string;
}
export const sendCargoReportByEmail = (idCargo: number, idCargoDetails: number[]) =>
  api.post<CargoReportRequestResponse>(
    `api/CargoDetail/SendReportByEmail/${idCargo}`,
    { idCargoDetails },
    { timeout: 20000 },
  );

// Images
// getImages se usa en el sync para deduplicar fotos ya subidas. Si falla
// el dedup, terminamos reintentando fotos que ya están en servidor →
// duplicados visibles para el usuario. Le damos 30s para que aguante en
// red débil.
export const getImages = (idCargoDetail: number) =>
  api.get(`api/Blob/GetImagesToAppController/${idCargoDetail}`, {
    timeout: 30000,
  });

export const uploadImages = (idCargoDetail: number, formData: FormData, config?: AxiosRequestConfig) =>
  api.post(`api/Blob/SaveMultiPartImages/${idCargoDetail}`, formData, {
    timeout: 120000,
    ...config,
  });

export const deleteImage = (idCargoDetail: number, imageName: string) =>
  api.delete<boolean>(`api/Blob/DeleteImage/${idCargoDetail}/${encodeURIComponent(imageName)}`);

// Documento de transporte (DDT) del proveedor, un PDF por contenedor
// (2026-10-23). Endpoints propios: los de fotos, que comparten la PWA y
// Android, no cambian. Sin DDT el servidor responde 204 y `data` llega vacío.
export const getDeliveryNote = (idCargoDetail: number) =>
  api.get<DeliveryNote | ''>(`api/DeliveryNote/GetByCargoDetail/${idCargoDetail}`, { timeout: 15000 });

export const getDeliveryNotesByCargo = (idCargo: number) =>
  api.get<DeliveryNote[]>(`api/DeliveryNote/GetByCargo/${idCargo}`, { timeout: 15000 });

export const uploadDeliveryNote = (idCargoDetail: number, file: Blob, fileName: string) => {
  const formData = new FormData();
  formData.append('file', file, fileName);
  return api.post<DeliveryNote>(`api/DeliveryNote/Upload/${idCargoDetail}`, formData, { timeout: 120000 });
};

export const deleteDeliveryNote = (idCargoDetail: number) =>
  api.delete<boolean>(`api/DeliveryNote/Delete/${idCargoDetail}`, { timeout: 30000 });

// OCR
export const readOcrField = (formData: FormData) =>
  api.post<OcrResult>('api/ocr/read-logistics-field', formData, {
    timeout: 30000,
  });

// VGM
// Proxy server-side en el backend de logística que reenvía a la Azure Function.
// Hacerlo desde el navegador al gateway de Azure falla por CORS.
export const sendVgmByDetail = (idCargoDetail: number) =>
  api.post<string>(`api/CargoDetail/SendVgmAppController/${idCargoDetail}`, null, {
    timeout: 60000,
  });

// Push notifications — registra (o actualiza) la subscripción del dispositivo
// del usuario autenticado. Idempotente: el cliente puede llamarlo en cada
// inicio de sesión sin crear filas duplicadas.
export const subscribePush = (body: {
  deviceId: string;
  platform: string;
  endpoint?: string;
  p256dh?: string;
  auth?: string;
  fcmToken?: string;
  userAgent?: string;
  appVersion?: string;
}) => api.post('api/PushNotifications/Subscribe', body);

// Marca como inactiva la subscripción de este dispositivo en el back. Se
// llama en el flujo de logout — el back saca el email del JWT, así que un
// user solo puede dar de baja sus propios dispositivos.
export const unsubscribePush = (body: { deviceId: string }) =>
  api.post('api/PushNotifications/Unsubscribe', body);

// Histórico de notificaciones recibidas por el usuario autenticado en las
// últimas N horas (default 48). Lo consume la pestaña Notificaciones —
// solo lectura, el back ya filtra por email del JWT y deduplica multi-device.
export interface MyNotification {
  idNotificationLog: number;
  sentAt: string; // ISO UTC
  title: string;
  body?: string | null;
  url?: string | null;
  source?: string | null;
}
export const getMyNotifications = (hours = 48) =>
  api.get<MyNotification[]>(`api/PushNotifications/MyLogs?hours=${hours}`);

// Persiste la confirmación de VGM (response del FunctionSendVgm) en el detalle:
// HasBeenSentXml=true, VgmConfirmation=mensaje, IdCargoState=4 (Cargada).
export const editCargoDetailVgmReference = (idCargoDetail: number, responseMessage: string, containerNumber: string) =>
  api.post(
    `api/CargoDetail/EditCargoDetailVgmReference/${idCargoDetail}/${encodeURIComponent(responseMessage)}/${encodeURIComponent(containerNumber)}`,
    null
  );
